import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { clearTemplateMetadataCache } from './widgets/index';
import ReactSwitch from 'react-switch';
import {
  Box,
  Typography,
  Paper,
  Button,
  Toolbar,
  IconButton,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  GridOn as GridIcon,
  Add as AddIcon,
  FlipToFront as BringToFrontIcon,
  FlipToBack as SendToBackIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  Delete as DeleteIcon,
  GroupWork as GroupIcon,
  UnfoldMore as ExpandIcon,
  ArrowBack as BackIcon,
  Widgets as WidgetsIcon,
  Settings as SettingsIcon,
  DataObject as MetaIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useParams, useHistory } from 'react-router-dom';
import widgetService from './services/widgetService';
import tagService from './services/tagService';
import SimplePropertyInspector from './SimplePropertyInspector';
import CanvasPropertyInspector from './CanvasPropertyInspector';
import { renderWidget } from './widgets';

const WidgetDesigner = () => {
  console.log('🎨 WidgetDesigner component rendering...');
  const { widgetId } = useParams();
  const history = useHistory();
  const navigate = (path) => history.push(path);
  const [isEditMode, setIsEditMode] = useState(!!widgetId);
  const [currentWidget, setCurrentWidget] = useState(null);
  
  // Widget configuration
  const [widgetName, setWidgetName] = useState('New Widget');
  const [widgetType, setWidgetType] = useState('custom');
  const [category, setCategory] = useState('custom');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  
  // Design canvas
  const [components, setComponents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [zoom, setZoom] = useState(100);
  
  // Canvas size for widget
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 200 });
  
  
  // Canvas properties
  const [canvasProperties, setCanvasProperties] = useState({
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#dddddd',
    borderRadius: 0,
    showGrid: true,
    gridSize: 20,
    gridColor: '#cccccc'
  });
  
  // Metadata and bindings
  const [availableTags, setAvailableTags] = useState([]);
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [availableCodeComponents, setAvailableCodeComponents] = useState([]);
  const [widgetMetadata, setWidgetMetadata] = useState({
    inputs: [], // { name, type, defaultValue, tagBinding }
    outputs: [], // { name, type, tagBinding }
    parameters: [], // { name, type, defaultValue, description }
    componentActions: {} // { componentId: { onClick, onChange, valuBinding, etc. } }
  });
  
  // Drag state for components
  const [dragState, setDragState] = useState({ 
    active: false, 
    componentId: null, 
    startX: 0, 
    startY: 0, 
    origX: 0, 
    origY: 0 
  });
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    componentId: null
  });
  
  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState(null);
  
  // Resize state
  const [resizeState, setResizeState] = useState({
    active: false,
    componentId: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    handle: null
  });
  
  // Group operations
  const [groupMode, setGroupMode] = useState(false);
  const [containers, setContainers] = useState([]);
  
  // Preview mode - when true, components are interactive, when false they're selectable
  const [previewMode, setPreviewMode] = useState(false);

  // Snap lines for alignment guides
  const [snapLines, setSnapLines] = useState({
    vertical: [],   // [{x: 100, type: 'left'}]
    horizontal: []  // [{y: 50, type: 'top'}]
  });
  const SNAP_THRESHOLD = 8;

  // Base widget types
  const baseComponentTypes = [
    { id: 'text-label', name: 'Text Label', icon: '🔤', defaultSize: { width: 100, height: 30 }, category: 'Display' },
    { id: 'text-box', name: 'Text Input', icon: '📝', defaultSize: { width: 120, height: 35 }, category: 'Input' },
    { id: 'button', name: 'Button', icon: '🔘', defaultSize: { width: 80, height: 35 }, category: 'Controls' },
    { id: 'led-indicator', name: 'LED', icon: '💡', defaultSize: { width: 30, height: 30 }, category: 'Indicators' },
    { id: 'progress-bar', name: 'Progress Bar', icon: '📊', defaultSize: { width: 150, height: 20 }, category: 'Indicators' },
    { id: 'gauge', name: 'Gauge', icon: '⏱️', defaultSize: { width: 120, height: 120 }, category: 'Indicators' },
    { id: 'toggle-switch', name: 'Toggle', icon: '🔄', defaultSize: { width: 60, height: 30 }, category: 'Controls' },
    { id: 'button-menu', name: 'Button Menu', icon: '🎛️', defaultSize: { width: 85, height: 75 }, category: 'Controls' },
    { id: 'set-value', name: 'Set Value', icon: '⚙️', defaultSize: { width: 120, height: 35 }, category: 'Controls' },
    { id: 'combo-box', name: 'Combo Box', icon: '📋', defaultSize: { width: 120, height: 35 }, category: 'Input' },
    { id: 'box', name: 'Rectangle', icon: '⬜', defaultSize: { width: 100, height: 60 }, category: 'Shapes' },
    { id: 'line', name: 'Line', icon: '➖', defaultSize: { width: 100, height: 2 }, category: 'Shapes' },
    { id: 'circle', name: 'Circle', icon: '⭕', defaultSize: { width: 60, height: 60 }, category: 'Shapes' }
  ];

  // Use only base component types with single category
  const componentTypes = useMemo(() => {
    // Change all categories to 'Controls'
    return baseComponentTypes.map(component => ({
      ...component,
      category: 'Controls'
    }));
  }, []);

  // Group component types by category
  const componentCategories = useMemo(() => {
    const groups = {};
    componentTypes.forEach(component => {
      const category = component.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(component);
    });
    return groups;
  }, [componentTypes]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName.toLowerCase() === 'input' || e.target.tagName.toLowerCase() === 'textarea') {
        return; // Don't interfere with input fields
      }

      // Copy (Ctrl+C)
      if (e.ctrlKey && e.key === 'c' && selectedId) {
        e.preventDefault();
        const comp = components.find(c => c.id === selectedId);
        if (comp) setClipboard({ ...comp, id: undefined });
      }
      
      // Paste (Ctrl+V)
      if (e.ctrlKey && e.key === 'v' && clipboard) {
        e.preventDefault();
        const newComponent = {
          ...clipboard,
          id: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          x: clipboard.x + 20,
          y: clipboard.y + 20,
          zIndex: components.length + 1
        };
        setComponents(prev => [...prev, newComponent]);
        setSelectedId(newComponent.id);
      }
      
      // Delete (Delete key)
      if (e.key === 'Delete' && selectedId) {
        e.preventDefault();
        setComponents(prev => prev.filter(c => c.id !== selectedId));
        setSelectedId(null);
      }

      // Arrow keys - move selected component by 1px
      if (selectedId && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const delta = e.shiftKey ? 10 : 1; // Shift ile 10px hareket
        setComponents(prev => prev.map(comp => {
          if (comp.id !== selectedId) return comp;
          let { x, y } = comp;
          switch (e.key) {
            case 'ArrowUp': y = Math.max(0, y - delta); break;
            case 'ArrowDown': y = y + delta; break;
            case 'ArrowLeft': x = Math.max(0, x - delta); break;
            case 'ArrowRight': x = x + delta; break;
            default: break;
          }
          return { ...comp, x, y, _updateKey: Date.now() };
        }));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, clipboard, components]);

  // Load widget data if editing
  useEffect(() => {
    const loadWidget = async () => {
      if (widgetId && isEditMode) {
        try {
          const widget = await widgetService.getCustomWidget(widgetId);
          setCurrentWidget(widget);
          setWidgetName(widget.widgetName);
          setWidgetType(widget.widgetType);
          setCategory(widget.category);
          setDescription(widget.description || '');
          setIsPublic(widget.isPublic);
          
          const config = widgetService.parseWidgetConfig(widget.widgetConfig);
          if (config.components) {
            setComponents(config.components);
          }
          if (config.canvasSize) {
            setCanvasSize(config.canvasSize);
          }
          if (config.metadata) {
            setWidgetMetadata(config.metadata);
          }
          if (config.canvasProperties) {
            setCanvasProperties(config.canvasProperties);
          }
        } catch (error) {
          console.error('Error loading widget:', error);
          toast.error('Error loading widget');
        }
      }
    };
    
    loadWidget();
  }, [widgetId, isEditMode]);

  // Helper function to load workflow/code details for input/output binding
  const loadWorkflowCodeDetails = async (actionType, name) => {
    console.log('Loading details for:', actionType, name);
    try {
      if (actionType === 'workflow') {
        const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5169/api'}/Task/workflows/1/${name}`;
        console.log('Fetching workflow from:', url);
        const response = await fetch(url);
        if (response.ok) {
          const workflow = await response.json();
          console.log('Workflow data received:', workflow);
          console.log('Raw workflow JSON string:', workflow.json);
          // Parse workflow JSON to extract inputs/outputs
          try {
            const workflowData = JSON.parse(workflow.json || '{}');
            console.log('Parsed workflow data:', workflowData);
            console.log('workflowData.localVars:', workflowData.localVars);
            console.log('All workflowData keys:', Object.keys(workflowData));
            
            // Extract inputs/outputs from workflow structure
            let inputs = [];
            let outputs = [];
            
            // For workflows: treat localVars as inputs that can be set by widget
            // This allows widget to pass tag values to workflow local variables
            if (workflowData.localVars && Array.isArray(workflowData.localVars)) {
              inputs = workflowData.localVars.map(variable => ({
                name: variable.name,
                type: variable.type.toLowerCase() === 'boolean' ? 'boolean' :
                      variable.type.toLowerCase() === 'float' || variable.type.toLowerCase() === 'double' ? 'number' : 'string',
                description: `Workflow variable: ${variable.name} (${variable.type}) - Set initial value from widget`
              }));

              console.log('📋 Workflow localVars mapped as inputs:', inputs);
            }
            
            // Workflow'lar için output gerek yok - direkt var_X değişkenlerine yazılır
            // Widget'lar workflow çalıştıktan sonra var_X değişkenlerini okuyabilir
            outputs = []; // Workflow'lar için boş bırakıyoruz

            // Extract workflow variables for output binding
            const workflowVariables = workflowData.localVars && Array.isArray(workflowData.localVars)
              ? workflowData.localVars.map(variable => ({
                  name: variable.name,
                  type: variable.type.toLowerCase() === 'boolean' ? 'boolean' :
                        variable.type.toLowerCase() === 'integer' ? 'number' :
                        variable.type.toLowerCase() === 'double' ? 'number' : 'string',
                  description: `Workflow variable: ${variable.name}`
                }))
              : [];

            const result = {
              inputs: inputs,
              outputs: outputs,
              workflowVariables: workflowVariables
            };
            console.log('Returning workflow inputs/outputs with variables:', result);
            return result;
          } catch (e) {
            console.warn('Could not parse workflow JSON:', e, 'Raw JSON:', workflow.json);
            return { inputs: [], outputs: [], workflowVariables: [] };
          }
        } else {
          console.error('Failed to fetch workflow:', response.status, response.statusText);
        }
      } else if (actionType === 'code') {
        const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5169/api'}/Task/code/1/${name}`;
        console.log('Fetching code component from:', url);
        const response = await fetch(url);
        if (response.ok) {
          const codeComponent = await response.json();
          console.log('Code component data received:', codeComponent);
          
          // Parse inputs and outputs from JSON strings
          let inputs = [];
          let outputs = [];
          
          try {
            const rawInputs = JSON.parse(codeComponent.inputs || '[]');
            inputs = Array.isArray(rawInputs) ? rawInputs.map(input => ({
              name: input.name || input.Name,
              type: (input.type || input.Type || 'string').toLowerCase(),
              description: input.description || input.Description || `Input parameter: ${input.name || input.Name}`
            })) : [];
            console.log('Parsed code inputs:', inputs);
          } catch (e) {
            console.warn('Could not parse code component inputs:', e);
          }
          
          try {
            const rawOutputs = JSON.parse(codeComponent.outputs || '[]');
            outputs = Array.isArray(rawOutputs) ? rawOutputs.map(output => ({
              name: output.name || output.Name,
              type: (output.type || output.Type || 'string').toLowerCase(),
              description: output.description || output.Description || `Output parameter: ${output.name || output.Name}`
            })) : [];
            console.log('Parsed code outputs:', outputs);
          } catch (e) {
            console.warn('Could not parse code component outputs:', e);
          }
          
          const result = {
            inputs: inputs,
            outputs: outputs,
            workflowVariables: [] // Code components don't have workflow variables
          };
          console.log('Returning code component inputs/outputs:', result);
          return result;
        } else {
          console.error('Failed to fetch code component:', response.status, response.statusText);
        }
      }
    } catch (error) {
      console.error('Error loading workflow/code details:', error);
    }
    return { inputs: [], outputs: [], workflowVariables: [] };
  };

  // Load workflows and code components
  useEffect(() => {
    const loadActionResources = async () => {
      try {
        // Load workflows from TaskController
        const workflowsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5169/api'}/Task/workflows/1`);
        if (workflowsResponse.ok) {
          const workflows = await workflowsResponse.json();
          setAvailableWorkflows(workflows.map(w => ({ 
            name: w.name, 
            displayName: w.name,
            id: w.id,
            description: w.description 
          })));
        }

        // Load code components from TaskController
        const componentsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5169/api'}/Task/codes/1`);
        if (componentsResponse.ok) {
          const components = await componentsResponse.json();
          setAvailableCodeComponents(components.map(c => ({ 
            name: c.name,
            displayName: c.name,
            id: c.id,
            language: c.language,
            description: c.description,
            methods: c.language === 'csharp' ? ['Execute', 'Process', 'Run'] : ['main', 'execute', 'process'] // Demo methods based on language
          })));
        }

        // Load tags
        const tagsResponse = await tagService.getTags();
        setAvailableTags(tagsResponse || []);
      } catch (error) {
        console.error('Error loading action resources:', error);
        // Fallback: Empty arrays if API is not available
        console.warn('Failed to load workflows and code components from API');
        setAvailableWorkflows([]);
        setAvailableCodeComponents([]);
      }
    };

    loadActionResources();
  }, []);

  // Load available tags
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await tagService.getTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error('Error loading tags:', error);
      }
    };
    
    loadTags();
  }, []);

  // No longer loading custom widgets for toolbox

  // Add component to canvas
  const handleAddComponent = useCallback((componentType) => {
    const typeInfo = componentTypes.find(t => t.id === componentType);
    if (!typeInfo) return;
    
    const newComponent = {
      id: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: componentType,
      x: 20 + (components.length * 10) % 200,
      y: 20 + (Math.floor(components.length / 20) * 40),
      width: typeInfo.defaultSize.width,
      height: typeInfo.defaultSize.height,
      properties: widgetService.getDefaultConfiguration(componentType),
      zIndex: components.length + 1,
      containerId: null
    };
    
    setComponents(prev => [...prev, newComponent]);
    setSelectedId(newComponent.id);
  }, [components.length, componentTypes]);

  const handleAddComponentAt = useCallback((componentType, x, y) => {
    const typeInfo = componentTypes.find(t => t.id === componentType);
    if (!typeInfo) return;
    
    const newComponent = {
      id: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: componentType,
      x: Math.max(0, Math.min(x, canvasSize.width - typeInfo.defaultSize.width)),
      y: Math.max(0, Math.min(y, canvasSize.height - typeInfo.defaultSize.height)),
      width: typeInfo.defaultSize.width,
      height: typeInfo.defaultSize.height,
      properties: widgetService.getDefaultConfiguration(componentType),
      zIndex: components.length + 1,
      containerId: null
    };
    
    setComponents(prev => [...prev, newComponent]);
    setSelectedId(newComponent.id);
  }, [components.length, componentTypes, canvasSize]);

  // Group selected components
  const handleGroupComponents = useCallback(() => {
    if (selectedGroup.length < 2) {
      toast.warning('Select at least 2 components to group');
      return;
    }
    
    const groupedComponents = components.filter(c => selectedGroup.includes(c.id));
    const bounds = calculateBounds(groupedComponents);
    
    const newContainer = {
      id: `container_${Date.now()}`,
      type: 'container',
      x: bounds.minX - 5,
      y: bounds.minY - 5,
      width: bounds.maxX - bounds.minX + 10,
      height: bounds.maxY - bounds.minY + 10,
      properties: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: '#ddd',
        borderStyle: 'dashed'
      },
      components: selectedGroup
    };
    
    // Update components to reference container
    setComponents(prev => prev.map(comp => 
      selectedGroup.includes(comp.id) 
        ? { ...comp, containerId: newContainer.id, x: comp.x - newContainer.x, y: comp.y - newContainer.y }
        : comp
    ));
    
    setContainers(prev => [...prev, newContainer]);
    setSelectedGroup([]);
    setGroupMode(false);
  }, [selectedGroup, components]);

  // Calculate bounds of components
  const calculateBounds = useCallback((comps) => {
    if (comps.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    
    const xs = comps.map(c => [c.x, c.x + c.width]).flat();
    const ys = comps.map(c => [c.y, c.y + c.height]).flat();
    
    return {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys)
    };
  }, []);

  // Save widget
  const handleSave = useCallback(async () => {
    try {
      const widgetConfig = {
        components,
        canvasSize,
        canvasProperties,
        metadata: widgetMetadata,
        containers
      };
      
      const widgetData = {
        widgetName,
        widgetType: widgetType || 'custom',
        category,
        description,
        isPublic,
        widgetConfig: JSON.stringify(widgetConfig),
        companyId: 1
      };
      
      if (isEditMode && widgetId) {
        await widgetService.updateCustomWidget(widgetId, widgetData);
        // Clear cache for this specific widget so changes are immediately visible
        clearTemplateMetadataCache(widgetId);
        toast.success('Widget updated successfully');
      } else {
        await widgetService.createCustomWidget(widgetData);
        // Clear all cache when creating new widget
        clearTemplateMetadataCache();
        toast.success('Widget created successfully');
      }

      navigate('/axisWidgets');
    } catch (error) {
      console.error('Error saving widget:', error);
      toast.error('Error saving widget');
    }
  }, [widgetName, widgetType, category, description, isPublic, components, canvasSize, widgetMetadata, containers, isEditMode, widgetId, navigate]);

  // Render component on canvas
  const renderComponent = useCallback((component) => {
    if (!component) return null;
    
    const isSelected = selectedId === component.id || selectedGroup.includes(component.id);
    
    return (
      <div
        key={`${component.id}-${component._updateKey || 0}`}
        style={{
          position: 'absolute',
          left: component.x,
          top: component.y,
          width: component.width,
          height: component.height,
          zIndex: component.zIndex || 1,
          border: isSelected && !previewMode ? '2px solid #1976d2' : '1px solid transparent',
          borderRadius: 2,
          cursor: previewMode ? 'default' : (dragState.active ? 'grabbing' : 'grab'),
          pointerEvents: previewMode ? 'auto' : 'all'
        }}
        onMouseDown={previewMode ? undefined : (e) => handleComponentMouseDown(e, component)}
        onClick={previewMode ? undefined : (e) => handleComponentClick(e, component)}
        onContextMenu={previewMode ? undefined : (e) => handleContextMenu(e, component)}
      >
        {renderWidget({
          type: component.type,
          properties: {
            ...component.properties,
            // Enable interactivity for TextLabel - make editable in preview mode
            ...(component.type === 'text-label' && previewMode && {
              text: component.properties?.text !== undefined && component.properties?.text !== null ? component.properties.text : 'Label',
              editable: true,
              onEdit: (newText) => {
                setComponents(prev => prev.map(c => 
                  c.id === component.id 
                    ? { ...c, properties: { ...c.properties, text: newText } }
                    : c
                ));
              }
            }),
            // Keep static text for edit mode
            ...(component.type === 'text-label' && !previewMode && {
              text: component.properties?.text !== undefined && component.properties?.text !== null ? component.properties.text : 'Label'
            }),
            // Enable interactivity for TextBox
            ...(component.type === 'text-box' && previewMode && {
              value: component.properties?.value || '',
              onChange: (e) => {
                setComponents(prev => prev.map(c => 
                  c.id === component.id 
                    ? { ...c, properties: { ...c.properties, value: e.target.value } }
                    : c
                ));
              }
            }),
            // Enable interactivity for LEDIndicator - simulate live state
            ...(component.type === 'led-indicator' && previewMode && {
              state: component.properties?.state !== undefined ? component.properties.state : 'active',
              onClick: () => {
                setComponents(prev => prev.map(c => 
                  c.id === component.id 
                    ? { 
                        ...c, 
                        properties: { 
                          ...c.properties, 
                          state: c.properties?.state === 'active' ? 'inactive' : 'active' 
                        } 
                      }
                    : c
                ));
              }
            }),
            // Static state for LED in edit mode
            ...(component.type === 'led-indicator' && !previewMode && {
              state: component.properties?.state !== undefined ? component.properties.state : 'active'
            }),
            // Enable interactivity for Button
            ...(component.type === 'button' && previewMode && {
              onClick: (e) => {
                e.stopPropagation();
                console.log(`Button ${component.id} clicked`);
                // Here we could trigger the action system
                const actionConfig = widgetMetadata.componentActions?.[component.id]?.onClick;
                if (actionConfig) {
                  console.log('Would execute action:', actionConfig);
                }
              }
            }),
            // Enable interactivity for ToggleSwitch
            ...(component.type === 'toggle-switch' && previewMode && {
              checked: component.properties?.checked || false,
              onChange: (checked) => {
                setComponents(prev => prev.map(c => 
                  c.id === component.id 
                    ? { ...c, properties: { ...c.properties, checked } }
                    : c
                ));
              }
            }),
            // Enable interactivity for ComboBox
            ...(component.type === 'combo-box' && previewMode && {
              selectedValue: component.properties?.selectedValue || '',
              onChange: (value) => {
                setComponents(prev => prev.map(c => 
                  c.id === component.id 
                    ? { ...c, properties: { ...c.properties, selectedValue: value } }
                    : c
                ));
              }
            }),
            // Enable interactivity for ProgressBar
            ...(component.type === 'progress-bar' && previewMode && {
              value: component.properties?.value || 50,
              onClick: () => {
                // Simulate value change for demo
                const newValue = Math.floor(Math.random() * 100);
                setComponents(prev => prev.map(c => 
                  c.id === component.id 
                    ? { ...c, properties: { ...c.properties, value: newValue } }
                    : c
                ));
              }
            }),
            // Enable interactivity for Gauge
            ...(component.type === 'gauge' && previewMode && {
              value: component.properties?.value || 0,
              onClick: () => {
                // Simulate value change for demo
                const newValue = Math.floor(Math.random() * 100);
                setComponents(prev => prev.map(c => 
                  c.id === component.id 
                    ? { ...c, properties: { ...c.properties, value: newValue } }
                    : c
                ));
              }
            })
          },
          width: component.width,
          height: component.height
        }, {}, previewMode)}
        
        {/* Resize handles */}
        {isSelected && !previewMode && (
          <>
            <div
              style={{
                position: 'absolute',
                right: -4,
                bottom: -4,
                width: 8,
                height: 8,
                backgroundColor: '#1976d2',
                cursor: 'se-resize',
                borderRadius: 1
              }}
              onMouseDown={(e) => handleResizeStart(e, component, 'se')}
            />
            <div
              style={{
                position: 'absolute',
                right: -4,
                top: component.height / 2 - 4,
                width: 8,
                height: 8,
                backgroundColor: '#1976d2',
                cursor: 'e-resize',
                borderRadius: 1
              }}
              onMouseDown={(e) => handleResizeStart(e, component, 'e')}
            />
            <div
              style={{
                position: 'absolute',
                left: component.width / 2 - 4,
                bottom: -4,
                width: 8,
                height: 8,
                backgroundColor: '#1976d2',
                cursor: 's-resize',
                borderRadius: 1
              }}
              onMouseDown={(e) => handleResizeStart(e, component, 's')}
            />
          </>
        )}
      </div>
    );
  }, [selectedId, selectedGroup, dragState.active, previewMode, widgetMetadata]);

  // Mouse event handlers
  const handleComponentMouseDown = useCallback((e, component) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (groupMode) {
      // Toggle selection in group mode
      setSelectedGroup(prev => 
        prev.includes(component.id) 
          ? prev.filter(id => id !== component.id)
          : [...prev, component.id]
      );
      return;
    }
    
    // Always set selected component immediately
    setSelectedId(component.id);
    
    // Only start drag if not in preview mode
    if (!previewMode) {
      setDragState({
        active: true,
        componentId: component.id,
        startX: e.clientX,
        startY: e.clientY,
        origX: component.x,
        origY: component.y
      });
    }
  }, [groupMode, previewMode]);

  const handleComponentClick = useCallback((e, component) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!previewMode) {
      // Ensure component is selected on click
      setSelectedId(component.id);
    }
  }, [previewMode]);

  const handleResizeStart = useCallback((e, component, handle) => {
    e.preventDefault();
    e.stopPropagation();
    
    setResizeState({
      active: true,
      componentId: component.id,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: component.width,
      startHeight: component.height,
      handle
    });
  }, []);

  const handleContextMenu = useCallback((e, component) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent canvas context menu from overriding
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      componentId: component.id
    });
  }, []);

  // Calculate snap lines for alignment guides
  const calculateSnapLines = useCallback((movingComponent, allComponents) => {
    const otherComponents = allComponents.filter(c => c.id !== movingComponent.id);

    // Moving component edges
    const moving = {
      left: movingComponent.x,
      right: movingComponent.x + movingComponent.width,
      top: movingComponent.y,
      bottom: movingComponent.y + movingComponent.height,
      centerX: movingComponent.x + movingComponent.width / 2,
      centerY: movingComponent.y + movingComponent.height / 2
    };

    let snapX = null, snapY = null;
    let minDiffX = SNAP_THRESHOLD, minDiffY = SNAP_THRESHOLD;
    const verticalLines = [], horizontalLines = [];

    otherComponents.forEach(other => {
      const edges = {
        left: other.x,
        right: other.x + other.width,
        top: other.y,
        bottom: other.y + other.height,
        centerX: other.x + other.width / 2,
        centerY: other.y + other.height / 2
      };

      // Check vertical alignments (X axis)
      const xChecks = [
        { movingEdge: 'left', otherEdge: 'left' },
        { movingEdge: 'left', otherEdge: 'right' },
        { movingEdge: 'right', otherEdge: 'left' },
        { movingEdge: 'right', otherEdge: 'right' },
        { movingEdge: 'centerX', otherEdge: 'centerX' }
      ];

      xChecks.forEach(check => {
        const diff = Math.abs(moving[check.movingEdge] - edges[check.otherEdge]);
        if (diff < SNAP_THRESHOLD) {
          verticalLines.push({ x: edges[check.otherEdge], type: check.otherEdge });
          if (diff < minDiffX) {
            minDiffX = diff;
            snapX = { value: edges[check.otherEdge], edge: check.movingEdge };
          }
        }
      });

      // Check horizontal alignments (Y axis)
      const yChecks = [
        { movingEdge: 'top', otherEdge: 'top' },
        { movingEdge: 'top', otherEdge: 'bottom' },
        { movingEdge: 'bottom', otherEdge: 'top' },
        { movingEdge: 'bottom', otherEdge: 'bottom' },
        { movingEdge: 'centerY', otherEdge: 'centerY' }
      ];

      yChecks.forEach(check => {
        const diff = Math.abs(moving[check.movingEdge] - edges[check.otherEdge]);
        if (diff < SNAP_THRESHOLD) {
          horizontalLines.push({ y: edges[check.otherEdge], type: check.otherEdge });
          if (diff < minDiffY) {
            minDiffY = diff;
            snapY = { value: edges[check.otherEdge], edge: check.movingEdge };
          }
        }
      });
    });

    // Remove duplicate lines
    const uniqueVertical = verticalLines.filter((line, index, self) =>
      index === self.findIndex(l => l.x === line.x)
    );
    const uniqueHorizontal = horizontalLines.filter((line, index, self) =>
      index === self.findIndex(l => l.y === line.y)
    );

    return { snapX, snapY, verticalLines: uniqueVertical, horizontalLines: uniqueHorizontal };
  }, [SNAP_THRESHOLD]);

  // Global mouse handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (dragState.active) {
        const deltaX = (e.clientX - dragState.startX) / (zoom / 100);
        const deltaY = (e.clientY - dragState.startY) / (zoom / 100);

        // Get current component for snap calculation
        const movingComp = components.find(c => c.id === dragState.componentId);
        if (movingComp) {
          const tempComp = {
            ...movingComp,
            x: dragState.origX + deltaX,
            y: dragState.origY + deltaY
          };

          // Calculate snap lines
          const { snapX, snapY, verticalLines, horizontalLines } = calculateSnapLines(tempComp, components);

          // Apply snap
          let newX = tempComp.x;
          let newY = tempComp.y;

          if (snapX) {
            if (snapX.edge === 'left') {
              newX = snapX.value;
            } else if (snapX.edge === 'right') {
              newX = snapX.value - movingComp.width;
            } else if (snapX.edge === 'centerX') {
              newX = snapX.value - movingComp.width / 2;
            }
          }

          if (snapY) {
            if (snapY.edge === 'top') {
              newY = snapY.value;
            } else if (snapY.edge === 'bottom') {
              newY = snapY.value - movingComp.height;
            } else if (snapY.edge === 'centerY') {
              newY = snapY.value - movingComp.height / 2;
            }
          }

          // Update snap lines state
          setSnapLines({ vertical: verticalLines, horizontal: horizontalLines });

          setComponents(prev => prev.map(comp =>
            comp.id === dragState.componentId
              ? { ...comp, x: newX, y: newY }
              : comp
          ));
        }
      } else if (resizeState.active) {
        const deltaX = (e.clientX - resizeState.startX) / (zoom / 100);
        const deltaY = (e.clientY - resizeState.startY) / (zoom / 100);

        setComponents(prev => prev.map(comp => {
          if (comp.id === resizeState.componentId) {
            const newComp = { ...comp };

            if (resizeState.handle.includes('e')) {
              newComp.width = Math.max(20, resizeState.startWidth + deltaX);
            }
            if (resizeState.handle.includes('s')) {
              newComp.height = Math.max(20, resizeState.startHeight + deltaY);
            }

            return newComp;
          }
          return comp;
        }));
      }
    };

    const handleMouseUp = () => {
      setDragState(prev => ({ ...prev, active: false }));
      setResizeState(prev => ({ ...prev, active: false }));
      // Clear snap lines when drag ends
      setSnapLines({ vertical: [], horizontal: [] });
    };

    if (dragState.active || resizeState.active) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, resizeState, zoom, components, calculateSnapLines]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ borderRadius: 0 }}>
        <Toolbar>
          <IconButton onClick={() => navigate('/axisWidgets')}>
            <BackIcon />
          </IconButton>
          <WidgetsIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {isEditMode ? `Edit Widget: ${widgetName}` : 'New Widget Designer'}
          </Typography>
          
          <IconButton onClick={() => setZoom(prev => Math.max(25, prev - 25))}>
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="body2" sx={{ mx: 1, minWidth: 50, textAlign: 'center' }}>
            {zoom}%
          </Typography>
          <IconButton onClick={() => setZoom(prev => Math.min(200, prev + 25))}>
            <ZoomInIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
            <ReactSwitch
              checked={canvasProperties.showGrid}
              onChange={(checked) => setCanvasProperties(prev => ({ ...prev, showGrid: checked }))}
              onColor="#edbc45"
              offColor="#ccc"
              height={20}
              width={40}
              handleDiameter={16}
              uncheckedIcon={false}
              checkedIcon={false}
            />
            <Typography variant="body2">Grid</Typography>
          </Box>
          
          <IconButton 
            onClick={() => setGroupMode(!groupMode)}
            color={groupMode ? 'primary' : 'default'}
          >
            <GroupIcon />
          </IconButton>
          
          {groupMode && selectedGroup.length >= 2 && (
            <Button onClick={handleGroupComponents} size="small">
              Group ({selectedGroup.length})
            </Button>
          )}
          
          <Button
            color="secondary"
            onClick={() => setPreviewMode(!previewMode)}
            variant={previewMode ? 'contained' : 'outlined'}
            size="small"
            sx={{ ml: 1, fontSize: '0.75rem', minWidth: 80 }}
          >
            {previewMode ? '🎯 Edit' : '👁️ Preview'}
          </Button>
          
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{ ml: 2, backgroundColor: '#edbc45', '&:hover': { backgroundColor: '#d4a83d' } }}
          >
            Save Widget
          </Button>
        </Toolbar>
      </Paper>

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Toolbox */}
        <Paper sx={{ width: 500, borderRadius: 0, overflowY: 'auto' }}>
          <Box sx={{ p: 1 }}>
            {/* Widget Properties */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <SettingsIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Widget Properties</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, py: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Widget Name"
                    value={widgetName}
                    onChange={(e) => setWidgetName(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      label="Category"
                      onChange={(e) => setCategory(e.target.value)}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {widgetService.getWidgetCategories().map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReactSwitch
                      checked={isPublic}
                      onChange={(checked) => setIsPublic(checked)}
                      onColor="#edbc45"
                      offColor="#ccc"
                      height={20}
                      width={40}
                      handleDiameter={16}
                      uncheckedIcon={false}
                      checkedIcon={false}
                    />
                    <Typography variant="body2">Public Widget</Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Canvas Size */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Typography variant="subtitle1">Canvas Size</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, py: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Width"
                    type="number"
                    value={canvasSize.width}
                    onChange={(e) => setCanvasSize(prev => ({ ...prev, width: parseInt(e.target.value) || 300 }))}
                    size="small"
                    inputProps={{ min: 100, max: 800 }}
                  />
                  <TextField
                    label="Height"
                    type="number"
                    value={canvasSize.height}
                    onChange={(e) => setCanvasSize(prev => ({ ...prev, height: parseInt(e.target.value) || 200 }))}
                    size="small"
                    inputProps={{ min: 100, max: 600 }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Action Bindings & Metadata */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Typography variant="subtitle1">Action Bindings & Metadata</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, py: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  
                  {/* Widget Inputs */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Widget Inputs</Typography>
                    {widgetMetadata.inputs?.map((input, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        alignItems: 'center',
                        mb: 1,
                        p: 1,
                        backgroundColor: '#f8f9fa',
                        borderRadius: 1
                      }}>
                        <TextField
                          size="small"
                          label="Name"
                          value={input.name || ''}
                          onChange={(e) => {
                            const newInputs = [...(widgetMetadata.inputs || [])];
                            newInputs[index] = { ...newInputs[index], name: e.target.value };
                            setWidgetMetadata(prev => ({ ...prev, inputs: newInputs }));
                          }}
                          sx={{ minWidth: 120 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={input.type || 'string'}
                            label="Type"
                            onChange={(e) => {
                              const newInputs = [...(widgetMetadata.inputs || [])];
                              newInputs[index] = { ...newInputs[index], type: e.target.value };
                              setWidgetMetadata(prev => ({ ...prev, inputs: newInputs }));
                            }}
                          >
                            <MenuItem value="string">String</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="boolean">Boolean</MenuItem>
                            <MenuItem value="tagId">Tag ID</MenuItem>
                            <MenuItem value="color">Color</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          label="Description"
                          value={input.description || ''}
                          onChange={(e) => {
                            const newInputs = [...(widgetMetadata.inputs || [])];
                            newInputs[index] = { ...newInputs[index], description: e.target.value };
                            setWidgetMetadata(prev => ({ ...prev, inputs: newInputs }));
                          }}
                          sx={{ flexGrow: 1 }}
                        />
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => {
                            const newInputs = widgetMetadata.inputs?.filter((_, i) => i !== index) || [];
                            setWidgetMetadata(prev => ({ ...prev, inputs: newInputs }));
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newInputs = [...(widgetMetadata.inputs || []), { name: '', type: 'string', description: '' }];
                        setWidgetMetadata(prev => ({ ...prev, inputs: newInputs }));
                      }}
                      sx={{ mt: 1 }}
                    >
                      Add Input
                    </Button>
                  </Box>

                  {/* Widget Outputs */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Widget Outputs</Typography>
                    {widgetMetadata.outputs?.map((output, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        alignItems: 'center',
                        mb: 1,
                        p: 1,
                        backgroundColor: '#f8f9fa',
                        borderRadius: 1
                      }}>
                        <TextField
                          size="small"
                          label="Name"
                          value={output.name || ''}
                          onChange={(e) => {
                            const newOutputs = [...(widgetMetadata.outputs || [])];
                            newOutputs[index] = { ...newOutputs[index], name: e.target.value };
                            setWidgetMetadata(prev => ({ ...prev, outputs: newOutputs }));
                          }}
                          sx={{ minWidth: 120 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={output.type || 'string'}
                            label="Type"
                            onChange={(e) => {
                              const newOutputs = [...(widgetMetadata.outputs || [])];
                              newOutputs[index] = { ...newOutputs[index], type: e.target.value };
                              setWidgetMetadata(prev => ({ ...prev, outputs: newOutputs }));
                            }}
                          >
                            <MenuItem value="string">String</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="boolean">Boolean</MenuItem>
                            <MenuItem value="event">Event</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          label="Description"
                          value={output.description || ''}
                          onChange={(e) => {
                            const newOutputs = [...(widgetMetadata.outputs || [])];
                            newOutputs[index] = { ...newOutputs[index], description: e.target.value };
                            setWidgetMetadata(prev => ({ ...prev, outputs: newOutputs }));
                          }}
                          sx={{ flexGrow: 1 }}
                        />
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => {
                            const newOutputs = widgetMetadata.outputs?.filter((_, i) => i !== index) || [];
                            setWidgetMetadata(prev => ({ ...prev, outputs: newOutputs }));
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newOutputs = [...(widgetMetadata.outputs || []), { name: '', type: 'string', description: '' }];
                        setWidgetMetadata(prev => ({ ...prev, outputs: newOutputs }));
                      }}
                      sx={{ mt: 1 }}
                    >
                      Add Output
                    </Button>
                  </Box>

                  {/* Component Actions */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Component Actions</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Define what actions each component should perform
                    </Typography>
                    
                    {components.map((component) => (
                      <Accordion key={component.id} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandIcon />}>
                          <Typography variant="body2">
                            {component.type}: {component.properties?.text || component.properties?.placeholder || component.id}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {/* onClick Action */}
                            {(component.type === 'button') && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Action Type</InputLabel>
                                  <Select
                                    value={widgetMetadata.componentActions?.[component.id]?.onClick?.actionType || ''}
                                    onChange={(e) => {
                                      setWidgetMetadata(prev => ({
                                        ...prev,
                                        componentActions: {
                                          ...prev.componentActions,
                                          [component.id]: {
                                            ...prev.componentActions?.[component.id],
                                            onClick: {
                                              actionType: e.target.value,
                                              // Clear previous selections when action type changes
                                              workflowName: '',
                                              componentName: '',
                                              method: '',
                                              parameters: {},
                                              pipeline: [],
                                              inputBindings: {},
                                              outputBindings: {},
                                              detailsCache: null
                                            }
                                          }
                                        }
                                      }));
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Select Action Type</em>
                                    </MenuItem>
                                    <MenuItem value="workflow">Workflow</MenuItem>
                                    <MenuItem value="code">Code Component</MenuItem>
                                    <MenuItem value="pipeline">Pipeline</MenuItem>
                                  </Select>
                                </FormControl>

                                {/* Workflow Configuration */}
                                {widgetMetadata.componentActions?.[component.id]?.onClick?.actionType === 'workflow' && (
                                  <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      Select a workflow to execute when this component is clicked. The workflow will run with the configured input parameters.
                                    </Typography>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Select Workflow</InputLabel>
                                      <Select
                                        value={widgetMetadata.componentActions?.[component.id]?.onClick?.workflowName || ''}
                                        onChange={(e) => {
                                          const workflowName = e.target.value;
                                          
                                          setWidgetMetadata(prev => ({
                                            ...prev,
                                            componentActions: {
                                              ...prev.componentActions,
                                              [component.id]: {
                                                ...prev.componentActions?.[component.id],
                                                onClick: {
                                                  ...prev.componentActions?.[component.id]?.onClick,
                                                  workflowName: workflowName
                                                }
                                              }
                                            }
                                          }));
                                          
                                          // Load workflow details for input/output binding asynchronously
                                          loadWorkflowCodeDetails('workflow', workflowName).then(details => {
                                            setWidgetMetadata(prev => ({
                                              ...prev,
                                              componentActions: {
                                                ...prev.componentActions,
                                                [component.id]: {
                                                  ...prev.componentActions?.[component.id],
                                                  onClick: {
                                                    ...prev.componentActions?.[component.id]?.onClick,
                                                    detailsCache: details // Cache the loaded details
                                                  }
                                                }
                                              }
                                            }));
                                          });
                                        }}
                                        label="Select Workflow"
                                      >
                                        {availableWorkflows.length === 0 ? (
                                          <MenuItem disabled>
                                            <em>No workflows available. Check if MetisEngine API is running.</em>
                                          </MenuItem>
                                        ) : (
                                          availableWorkflows.map(workflow => (
                                            <MenuItem key={workflow.name} value={workflow.name}>
                                              {workflow.displayName || workflow.name}
                                            </MenuItem>
                                          ))
                                        )}
                                      </Select>
                                    </FormControl>
                                    <TextField
                                      size="small"
                                      label="Parameters (JSON)"
                                      value={JSON.stringify(widgetMetadata.componentActions?.[component.id]?.onClick?.parameters || {})}
                                      onChange={(e) => {
                                        try {
                                          const params = JSON.parse(e.target.value || '{}');
                                          setWidgetMetadata(prev => ({
                                            ...prev,
                                            componentActions: {
                                              ...prev.componentActions,
                                              [component.id]: {
                                                ...prev.componentActions?.[component.id],
                                                onClick: {
                                                  ...prev.componentActions?.[component.id]?.onClick,
                                                  parameters: params
                                                }
                                              }
                                            }
                                          }));
                                        } catch (error) {
                                          // Invalid JSON, ignore
                                        }
                                      }}
                                      fullWidth
                                      multiline
                                      rows={2}
                                      placeholder='{"param1": "value1", "param2": "@inputs.value"}'
                                      helperText="JSON object with workflow parameters. Use @inputs.name, @tags[id], @variables.name for bindings"
                                    />
                                  </>
                                )}

                                {/* Code Component Configuration */}
                                {widgetMetadata.componentActions?.[component.id]?.onClick?.actionType === 'code' && (
                                  <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      Select a code component and method to execute when this component is clicked. The code will run with the configured input parameters.
                                    </Typography>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Select Component</InputLabel>
                                      <Select
                                        value={widgetMetadata.componentActions?.[component.id]?.onClick?.componentName || ''}
                                        onChange={(e) => {
                                          const componentName = e.target.value;
                                          
                                          setWidgetMetadata(prev => ({
                                            ...prev,
                                            componentActions: {
                                              ...prev.componentActions,
                                              [component.id]: {
                                                ...prev.componentActions?.[component.id],
                                                onClick: {
                                                  ...prev.componentActions?.[component.id]?.onClick,
                                                  componentName: componentName,
                                                  method: '' // Reset method when component changes
                                                }
                                              }
                                            }
                                          }));
                                          
                                          // Load code component details for input/output binding asynchronously
                                          loadWorkflowCodeDetails('code', componentName).then(details => {
                                            setWidgetMetadata(prev => ({
                                              ...prev,
                                              componentActions: {
                                                ...prev.componentActions,
                                                [component.id]: {
                                                  ...prev.componentActions?.[component.id],
                                                  onClick: {
                                                    ...prev.componentActions?.[component.id]?.onClick,
                                                    detailsCache: details // Cache the loaded details
                                                  }
                                                }
                                              }
                                            }));
                                          });
                                        }}
                                        label="Select Component"
                                      >
                                        {availableCodeComponents.length === 0 ? (
                                          <MenuItem disabled>
                                            <em>No code components available. Check if MetisEngine API is running.</em>
                                          </MenuItem>
                                        ) : (
                                          availableCodeComponents.map(comp => (
                                            <MenuItem key={comp.name} value={comp.name}>
                                              {comp.displayName || comp.name} ({comp.language})
                                            </MenuItem>
                                          ))
                                        )}
                                      </Select>
                                    </FormControl>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Select Method</InputLabel>
                                      <Select
                                        value={widgetMetadata.componentActions?.[component.id]?.onClick?.method || ''}
                                        onChange={(e) => {
                                          setWidgetMetadata(prev => ({
                                            ...prev,
                                            componentActions: {
                                              ...prev.componentActions,
                                              [component.id]: {
                                                ...prev.componentActions?.[component.id],
                                                onClick: {
                                                  ...prev.componentActions?.[component.id]?.onClick,
                                                  method: e.target.value
                                                }
                                              }
                                            }
                                          }));
                                        }}
                                        label="Select Method"
                                      >
                                        {(() => {
                                          const selectedComponent = availableCodeComponents.find(
                                            comp => comp.name === widgetMetadata.componentActions?.[component.id]?.onClick?.componentName
                                          );
                                          if (!selectedComponent) {
                                            return (
                                              <MenuItem disabled>
                                                <em>Select a component first</em>
                                              </MenuItem>
                                            );
                                          }
                                          if (!selectedComponent.methods || selectedComponent.methods.length === 0) {
                                            return (
                                              <MenuItem disabled>
                                                <em>No methods available for this component</em>
                                              </MenuItem>
                                            );
                                          }
                                          return selectedComponent.methods.map(method => (
                                            <MenuItem key={method} value={method}>
                                              {method}
                                            </MenuItem>
                                          ));
                                        })()}
                                      </Select>
                                    </FormControl>
                                    <TextField
                                      size="small"
                                      label="Parameters (JSON)"
                                      value={JSON.stringify(widgetMetadata.componentActions?.[component.id]?.onClick?.parameters || {})}
                                      onChange={(e) => {
                                        try {
                                          const params = JSON.parse(e.target.value || '{}');
                                          setWidgetMetadata(prev => ({
                                            ...prev,
                                            componentActions: {
                                              ...prev.componentActions,
                                              [component.id]: {
                                                ...prev.componentActions?.[component.id],
                                                onClick: {
                                                  ...prev.componentActions?.[component.id]?.onClick,
                                                  parameters: params
                                                }
                                              }
                                            }
                                          }));
                                        } catch (error) {
                                          // Invalid JSON, ignore
                                        }
                                      }}
                                      fullWidth
                                      multiline
                                      rows={2}
                                      placeholder='{"speed": 100, "duration": "@inputs.time"}'
                                      helperText="JSON object with method parameters. Use @inputs.name, @tags[id], @variables.name for bindings"
                                    />
                                  </>
                                )}

                                {/* Pipeline Configuration */}
                                {widgetMetadata.componentActions?.[component.id]?.onClick?.actionType === 'pipeline' && (
                                  <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      Pipeline chains multiple workflows/code components in sequence. Each step's output becomes the next step's input.
                                    </Typography>
                                    <TextField
                                      size="small"
                                      label="Pipeline Steps (JSON)"
                                      value={JSON.stringify(widgetMetadata.componentActions?.[component.id]?.onClick?.pipeline || [])}
                                      onChange={(e) => {
                                        try {
                                          const pipeline = JSON.parse(e.target.value || '[]');
                                          setWidgetMetadata(prev => ({
                                            ...prev,
                                            componentActions: {
                                              ...prev.componentActions,
                                              [component.id]: {
                                                ...prev.componentActions?.[component.id],
                                                onClick: {
                                                  ...prev.componentActions?.[component.id]?.onClick,
                                                  pipeline: pipeline
                                                }
                                              }
                                            }
                                          }));
                                        } catch (error) {
                                          // Invalid JSON, ignore
                                        }
                                      }}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      placeholder='[{"type": "workflow", "name": "DataValidationWorkflow", "parameters": {"input": "@inputs.value"}}, {"type": "code", "componentName": "DataProcessor", "method": "ProcessValue", "parameters": {"data": "@step[0].result"}}]'
                                      helperText="Array of pipeline steps. Use @inputs.name for widget inputs, @step[n].result for previous step outputs"
                                    />
                                  </>
                                )}

                                {/* Input/Output Binding Configuration */}
                                {(widgetMetadata.componentActions?.[component.id]?.onClick?.actionType === 'workflow' || 
                                  widgetMetadata.componentActions?.[component.id]?.onClick?.actionType === 'code') && (
                                  <>
                                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                      Data Binding Configuration
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                      Configure how widget data flows to/from the selected {widgetMetadata.componentActions?.[component.id]?.onClick?.actionType}.
                                      Input bindings send widget data to the {widgetMetadata.componentActions?.[component.id]?.onClick?.actionType}, 
                                      and output bindings update the widget with results.
                                    </Typography>
                                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                        💡 How it works:
                                      </Typography>
                                      <Typography variant="body2" component="div">
                                        • <strong>Input Bindings</strong>: Widget values → {widgetMetadata.componentActions?.[component.id]?.onClick?.actionType} parameters<br/>
                                        • <strong>Output Bindings</strong>: {widgetMetadata.componentActions?.[component.id]?.onClick?.actionType} results → Widget properties<br/>
                                        • <strong>Example</strong>: Button click → Send sensor value to workflow → Update LED color with result
                                      </Typography>
                                    </Box>

                                    {/* Input Bindings */}
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Input Bindings (Widget → {widgetMetadata.componentActions?.[component.id]?.onClick?.actionType === 'workflow' ? 'Workflow' : 'Code'})
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                      {(() => {
                                        // Get inputs based on action type
                                        const actionType = widgetMetadata.componentActions?.[component.id]?.onClick?.actionType;
                                        const selectedName = actionType === 'workflow' 
                                          ? widgetMetadata.componentActions?.[component.id]?.onClick?.workflowName
                                          : widgetMetadata.componentActions?.[component.id]?.onClick?.componentName;
                                        
                                        // Get cached inputs from API
                                        const cachedDetails = widgetMetadata.componentActions?.[component.id]?.onClick?.detailsCache;
                                        const hasRealData = cachedDetails?.inputs && cachedDetails.inputs.length >= 0; // Allow empty arrays
                                        
                                        const inputs = hasRealData ? cachedDetails.inputs : [];

                                        return selectedName ? (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {inputs.length === 0 ? (
                                              <Typography variant="body2" color="text.secondary">
                                                No input parameters defined for this {actionType}.
                                              </Typography>
                                            ) : (
                                              inputs.map((input, idx) => (
                                              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 'medium' }}>
                                                  {input.name} ({input.type}):
                                                </Typography>
                                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                                  <Select
                                                    value={widgetMetadata.componentActions?.[component.id]?.onClick?.inputBindings?.[input.name]?.source || ''}
                                                    onChange={(e) => {
                                                      setWidgetMetadata(prev => ({
                                                        ...prev,
                                                        componentActions: {
                                                          ...prev.componentActions,
                                                          [component.id]: {
                                                            ...prev.componentActions?.[component.id],
                                                            onClick: {
                                                              ...prev.componentActions?.[component.id]?.onClick,
                                                              inputBindings: {
                                                                ...prev.componentActions?.[component.id]?.onClick?.inputBindings,
                                                                [input.name]: {
                                                                  source: e.target.value,
                                                                  type: input.type
                                                                }
                                                              }
                                                            }
                                                          }
                                                        }
                                                      }));
                                                    }}
                                                    displayEmpty
                                                  >
                                                    <MenuItem value="">
                                                      <em>Select source...</em>
                                                    </MenuItem>
                                                    <MenuItem value={`widget.properties.${component.type === 'text-label' ? 'text' : component.type === 'led-indicator' ? 'state' : 'value'}`}>
                                                      Widget Value
                                                    </MenuItem>
                                                    <MenuItem value="widget.width">Widget Width</MenuItem>
                                                    <MenuItem value="widget.height">Widget Height</MenuItem>
                                                    <MenuItem value="widget.x">Widget X Position</MenuItem>
                                                    <MenuItem value="widget.y">Widget Y Position</MenuItem>
                                                    {widgetMetadata.inputs.map(inp => (
                                                      <MenuItem key={inp.name} value={`inputs.${inp.name}`}>
                                                        Input: {inp.name}
                                                      </MenuItem>
                                                    ))}
                                                    {availableTags.slice(0, 5).map(tag => (
                                                      <MenuItem key={tag.id} value={`tags.${tag.id}`}>
                                                        Tag: {tag.name}
                                                      </MenuItem>
                                                    ))}
                                                  </Select>
                                                </FormControl>
                                                <TextField
                                                  size="small"
                                                  placeholder="Custom value or expression"
                                                  value={widgetMetadata.componentActions?.[component.id]?.onClick?.inputBindings?.[input.name]?.customValue || ''}
                                                  onChange={(e) => {
                                                    setWidgetMetadata(prev => ({
                                                      ...prev,
                                                      componentActions: {
                                                        ...prev.componentActions,
                                                        [component.id]: {
                                                          ...prev.componentActions?.[component.id],
                                                          onClick: {
                                                            ...prev.componentActions?.[component.id]?.onClick,
                                                            inputBindings: {
                                                              ...prev.componentActions?.[component.id]?.onClick?.inputBindings,
                                                              [input.name]: {
                                                                ...prev.componentActions?.[component.id]?.onClick?.inputBindings?.[input.name],
                                                                customValue: e.target.value
                                                              }
                                                            }
                                                          }
                                                        }
                                                      }
                                                    }));
                                                  }}
                                                  sx={{ flex: 1 }}
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200 }}>
                                                  {input.description}
                                                </Typography>
                                              </Box>
                                            ))
                                            )}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            Please select a {actionType} first to configure input bindings.
                                          </Typography>
                                        );
                                      })()}
                                    </Box>

                                    {/* Output Bindings */}
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Output Bindings ({widgetMetadata.componentActions?.[component.id]?.onClick?.actionType === 'workflow' ? 'Workflow' : 'Code'} → Widget)
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                      {(() => {
                                        const actionType = widgetMetadata.componentActions?.[component.id]?.onClick?.actionType;
                                        const selectedName = actionType === 'workflow' 
                                          ? widgetMetadata.componentActions?.[component.id]?.onClick?.workflowName
                                          : widgetMetadata.componentActions?.[component.id]?.onClick?.componentName;
                                        
                                        // Get cached outputs from API
                                        const cachedDetails = widgetMetadata.componentActions?.[component.id]?.onClick?.detailsCache;
                                        // Check if we have real data for outputs or workflow variables
                                        const hasRealData = actionType === 'workflow' ?
                                          (cachedDetails?.workflowVariables && cachedDetails.workflowVariables.length >= 0) :
                                          (cachedDetails?.outputs && cachedDetails.outputs.length >= 0);
                                        
                                        // Get cached outputs from API
                                        // For workflows, use workflowVariables as outputs
                                        const outputs = hasRealData ?
                                          (actionType === 'workflow' ?
                                            (cachedDetails.workflowVariables || []) :
                                            cachedDetails.outputs) : [];

                                        return selectedName ? (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {outputs.length === 0 ? (
                                              <Typography variant="body2" color="text.secondary">
                                                No output parameters defined for this {actionType}.
                                              </Typography>
                                            ) : (
                                              outputs.map((output, idx) => (
                                              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 'medium' }}>
                                                  {output.name} ({output.type}):
                                                </Typography>
                                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                                  <Select
                                                    value={widgetMetadata.componentActions?.[component.id]?.onClick?.outputBindings?.[output.name]?.target || ''}
                                                    onChange={(e) => {
                                                      setWidgetMetadata(prev => ({
                                                        ...prev,
                                                        componentActions: {
                                                          ...prev.componentActions,
                                                          [component.id]: {
                                                            ...prev.componentActions?.[component.id],
                                                            onClick: {
                                                              ...prev.componentActions?.[component.id]?.onClick,
                                                              outputBindings: {
                                                                ...prev.componentActions?.[component.id]?.onClick?.outputBindings,
                                                                [output.name]: {
                                                                  target: e.target.value,
                                                                  type: output.type
                                                                }
                                                              }
                                                            }
                                                          }
                                                        }
                                                      }));
                                                    }}
                                                    displayEmpty
                                                  >
                                                    <MenuItem value="">
                                                      <em>Select target...</em>
                                                    </MenuItem>
                                                    <MenuItem value={`widget.properties.${component.type === 'text-label' ? 'text' : component.type === 'led-indicator' ? 'state' : 'value'}`}>
                                                      Widget Value
                                                    </MenuItem>
                                                    <MenuItem value="widget.properties.backgroundColor">Background Color</MenuItem>
                                                    <MenuItem value="widget.properties.foreColor">Text Color</MenuItem>
                                                    {component.type === 'led-indicator' && (
                                                      <>
                                                        <MenuItem value="widget.properties.color">LED Color</MenuItem>
                                                        <MenuItem value="widget.properties.blink">LED Blink</MenuItem>
                                                      </>
                                                    )}
                                                    {widgetMetadata.outputs.map(out => (
                                                      <MenuItem key={out.name} value={`outputs.${out.name}`}>
                                                        Output: {out.name}
                                                      </MenuItem>
                                                    ))}
                                                    {/* Dynamic Workflow Variables */}
                                                    {actionType === 'workflow' && cachedDetails?.workflowVariables && (
                                                      cachedDetails.workflowVariables.map(variable => (
                                                        <MenuItem key={variable.name} value={`variables.${variable.name}`}>
                                                          Variable: {variable.name} ({variable.type})
                                                        </MenuItem>
                                                      ))
                                                    )}
                                                    {/* Fallback static variables if no dynamic ones */}
                                                    {(!cachedDetails?.workflowVariables || cachedDetails.workflowVariables.length === 0) && (
                                                      <>
                                                        <MenuItem value="variables.lastResult">
                                                          Variable: lastResult
                                                        </MenuItem>
                                                        <MenuItem value="variables.processStatus">
                                                          Variable: processStatus
                                                        </MenuItem>
                                                        <MenuItem value="variables.errorMessage">
                                                          Variable: errorMessage
                                                        </MenuItem>
                                                        <MenuItem value="variables.calculatedValue">
                                                          Variable: calculatedValue
                                                        </MenuItem>
                                                        <MenuItem value="variables.alarmState">
                                                          Variable: alarmState
                                                        </MenuItem>
                                                      </>
                                                    )}
                                                    {availableTags.slice(0, 5).map(tag => (
                                                      <MenuItem key={tag.id} value={`tags.${tag.id}`}>
                                                        Tag: {tag.name}
                                                      </MenuItem>
                                                    ))}
                                                  </Select>
                                                </FormControl>
                                                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200 }}>
                                                  {output.description}
                                                </Typography>
                                              </Box>
                                            ))
                                            )}
                                            
                                            {/* Auto-update option */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <ReactSwitch
                                                checked={widgetMetadata.componentActions?.[component.id]?.onClick?.autoUpdateWidget || false}
                                                onChange={(checked) => {
                                                  setWidgetMetadata(prev => ({
                                                    ...prev,
                                                    componentActions: {
                                                      ...prev.componentActions,
                                                      [component.id]: {
                                                        ...prev.componentActions?.[component.id],
                                                        onClick: {
                                                          ...prev.componentActions?.[component.id]?.onClick,
                                                          autoUpdateWidget: checked
                                                        }
                                                      }
                                                    }
                                                  }));
                                                }}
                                                onColor="#edbc45"
                                                offColor="#ccc"
                                                height={20}
                                                width={40}
                                                handleDiameter={16}
                                                uncheckedIcon={false}
                                                checkedIcon={false}
                                              />
                                              <Typography variant="body2">Auto-update widget with outputs</Typography>
                                            </Box>
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            Please select a {actionType} first to configure output bindings.
                                          </Typography>
                                        );
                                      })()}
                                    </Box>
                                  </>
                                )}

                              </Box>
                            )}
                            
                            {/* onChange Action */}
                            {(component.type === 'text-box' || component.type === 'toggle-switch' || component.type === 'combo-box') && (
                              <TextField
                                size="small"
                                label="onChange Action"
                                multiline
                                rows={2}
                                value={widgetMetadata.componentActions?.[component.id]?.onChange || ''}
                                onChange={(e) => {
                                  setWidgetMetadata(prev => ({
                                    ...prev,
                                    componentActions: {
                                      ...prev.componentActions,
                                      [component.id]: {
                                        ...prev.componentActions?.[component.id],
                                        onChange: e.target.value
                                      }
                                    }
                                  }));
                                }}
                                placeholder="@variables.userInput = $event.value;"
                                helperText="Use $event.value for the changed value"
                                fullWidth
                              />
                            )}
                            
                            {/* Property Bindings */}
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Property Bindings
                              </Typography>
                              {Object.entries(widgetMetadata.componentActions?.[component.id]?.propertyBindings || {}).map(([property, binding], index) => (
                                <Paper key={`${component.id}-${property}-${index}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        Property Binding #{index + 1}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setWidgetMetadata(prev => {
                                            const currentBindings = { ...prev.componentActions?.[component.id]?.propertyBindings };
                                            delete currentBindings[property];
                                            return {
                                              ...prev,
                                              componentActions: {
                                                ...prev.componentActions,
                                                [component.id]: {
                                                  ...prev.componentActions?.[component.id],
                                                  propertyBindings: currentBindings
                                                }
                                              }
                                            };
                                          });
                                        }}
                                      >
                                        🗑️
                                      </IconButton>
                                    </Box>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Property</InputLabel>
                                      <Select
                                        value={property}
                                        label="Property"
                                        onChange={(e) => {
                                          const oldProperty = property;
                                          const newProperty = e.target.value;
                                          console.log('🔄 Select onChange triggered:', {
                                            oldProperty,
                                            newProperty,
                                            componentId: component.id,
                                            componentType: component.type,
                                            event: e
                                          });

                                          if (oldProperty === newProperty) {
                                            console.log('⚠️ Same property selected, skipping update');
                                            return;
                                          }

                                          setWidgetMetadata(prev => {
                                            const currentBindings = { ...prev.componentActions?.[component.id]?.propertyBindings };
                                            currentBindings[newProperty] = currentBindings[oldProperty];
                                            delete currentBindings[oldProperty];

                                            console.log('📝 Updating bindings:', {
                                              from: oldProperty,
                                              to: newProperty,
                                              currentBindings
                                            });

                                            return {
                                              ...prev,
                                              componentActions: {
                                                ...prev.componentActions,
                                                [component.id]: {
                                                  ...prev.componentActions?.[component.id],
                                                  propertyBindings: currentBindings
                                                }
                                              }
                                            };
                                          });
                                        }}
                                      >
                                      {(() => {
                                        // Define all available properties for each component type
                                        const commonProperties = [
                                          { value: 'tagValue', label: 'Value/State (tagValue)' },
                                          { value: 'color', label: 'Color' },
                                          { value: 'backgroundColor', label: 'Background Color' },
                                          { value: 'foreColor', label: 'Text Color' },
                                          { value: 'disabled', label: 'Disabled' },
                                          { value: 'opacity', label: 'Opacity' }
                                        ];

                                        const componentSpecificProperties = {
                                          'text-label': [
                                            { value: 'text', label: 'Text Content' },
                                            { value: 'fontSize', label: 'Font Size' },
                                            { value: 'fontWeight', label: 'Font Weight' }
                                          ],
                                          'led-indicator': [
                                            { value: 'size', label: 'Size' },
                                            { value: 'blink', label: 'Blink' },
                                            { value: 'offColor', label: 'Off Color' }
                                          ],
                                          'button': [
                                            { value: 'text', label: 'Button Text' },
                                            { value: 'variant', label: 'Variant' }
                                          ],
                                          'text-box': [
                                            { value: 'value', label: 'Input Value' },
                                            { value: 'placeholder', label: 'Placeholder' },
                                            { value: 'readOnly', label: 'Read Only' }
                                          ],
                                          'progress-bar': [
                                            { value: 'value', label: 'Progress Value' },
                                            { value: 'min', label: 'Minimum Value' },
                                            { value: 'max', label: 'Maximum Value' }
                                          ],
                                          'gauge': [
                                            { value: 'value', label: 'Gauge Value' },
                                            { value: 'min', label: 'Minimum Value' },
                                            { value: 'max', label: 'Maximum Value' },
                                            { value: 'unit', label: 'Unit' }
                                          ]
                                        };

                                        const allProperties = [
                                          ...commonProperties,
                                          ...(componentSpecificProperties[component.type] || [])
                                        ];

                                        console.log('🎯 Rendering MenuItems for component:', component.type, 'Total items:', allProperties.length);

                                        return allProperties.map((prop, index) => (
                                          <MenuItem
                                            key={`${component.id}-${prop.value}-${index}`}
                                            value={prop.value}
                                          >
                                            {prop.label}
                                          </MenuItem>
                                        ));
                                      })()}
                                    </Select>
                                  </FormControl>
                                    {/* Enhanced binding with type selection and mapping */}
                                    {(() => {
                                      // Handle both old string format and new object format
                                      const bindingConfig = typeof binding === 'string' ? { source: binding, type: 'direct' } : binding || {};
                                      const bindingType = bindingConfig.type || 'direct';
                                      const mappingTable = bindingConfig.mappingTable || {};

                                      return (
                                        <>
                                          <TextField
                                            size="small"
                                            label="Binding Source"
                                            value={bindingConfig.source || ''}
                                            onChange={(e) => {
                                              setWidgetMetadata(prev => ({
                                                ...prev,
                                                componentActions: {
                                                  ...prev.componentActions,
                                                  [component.id]: {
                                                    ...prev.componentActions?.[component.id],
                                                    propertyBindings: {
                                                      ...prev.componentActions?.[component.id]?.propertyBindings,
                                                      [property]: {
                                                        ...bindingConfig,
                                                        source: e.target.value
                                                      }
                                                    }
                                                  }
                                                }
                                              }));
                                            }}
                                            placeholder="@inputs.value"
                                            helperText="Bind to @inputs.variableName or @tags[tagId]"
                                            fullWidth
                                          />

                                          <FormControl size="small" fullWidth>
                                            <InputLabel>Binding Type</InputLabel>
                                            <Select
                                              value={bindingType}
                                              label="Binding Type"
                                              onChange={(e) => {
                                                const newType = e.target.value;
                                                setWidgetMetadata(prev => ({
                                                  ...prev,
                                                  componentActions: {
                                                    ...prev.componentActions,
                                                    [component.id]: {
                                                      ...prev.componentActions?.[component.id],
                                                      propertyBindings: {
                                                        ...prev.componentActions?.[component.id]?.propertyBindings,
                                                        [property]: {
                                                          source: bindingConfig.source || '',
                                                          type: newType,
                                                          mappingTable: newType === 'direct' ? {} : mappingTable
                                                        }
                                                      }
                                                    }
                                                  }
                                                }));
                                              }}
                                            >
                                              <MenuItem value="direct">Direct Value</MenuItem>
                                              <MenuItem value="boolean">Boolean Mapping</MenuItem>
                                              <MenuItem value="range">Range Mapping</MenuItem>
                                            </Select>
                                          </FormControl>

                                          {/* Boolean Mapping Table */}
                                          {bindingType === 'boolean' && (
                                            <Box sx={{ mt: 2 }}>
                                              <Typography variant="caption">Boolean Mapping</Typography>
                                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                <TextField
                                                  size="small"
                                                  label="True Value"
                                                  value={mappingTable.true || ''}
                                                  onChange={(e) => {
                                                    setWidgetMetadata(prev => ({
                                                      ...prev,
                                                      componentActions: {
                                                        ...prev.componentActions,
                                                        [component.id]: {
                                                          ...prev.componentActions?.[component.id],
                                                          propertyBindings: {
                                                            ...prev.componentActions?.[component.id]?.propertyBindings,
                                                            [property]: {
                                                              ...bindingConfig,
                                                              mappingTable: { ...mappingTable, true: e.target.value }
                                                            }
                                                          }
                                                        }
                                                      }
                                                    }));
                                                  }}
                                                  sx={{ flex: 1 }}
                                                />
                                                <TextField
                                                  size="small"
                                                  label="False Value"
                                                  value={mappingTable.false || ''}
                                                  onChange={(e) => {
                                                    setWidgetMetadata(prev => ({
                                                      ...prev,
                                                      componentActions: {
                                                        ...prev.componentActions,
                                                        [component.id]: {
                                                          ...prev.componentActions?.[component.id],
                                                          propertyBindings: {
                                                            ...prev.componentActions?.[component.id]?.propertyBindings,
                                                            [property]: {
                                                              ...bindingConfig,
                                                              mappingTable: { ...mappingTable, false: e.target.value }
                                                            }
                                                          }
                                                        }
                                                      }
                                                    }));
                                                  }}
                                                  sx={{ flex: 1 }}
                                                />
                                              </Box>
                                            </Box>
                                          )}

                                          {/* Range Mapping Table */}
                                          {bindingType === 'range' && (
                                            <Box sx={{ mt: 2 }}>
                                              <Typography variant="caption">Range Mapping</Typography>
                                              {(mappingTable.ranges || []).map((range, idx) => (
                                                <Box key={idx} sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                  <TextField
                                                    size="small"
                                                    label="Min"
                                                    type="number"
                                                    value={range.min || 0}
                                                    onChange={(e) => {
                                                      const newRanges = [...(mappingTable.ranges || [])];
                                                      newRanges[idx] = { ...newRanges[idx], min: Number(e.target.value) };
                                                      setWidgetMetadata(prev => ({
                                                        ...prev,
                                                        componentActions: {
                                                          ...prev.componentActions,
                                                          [component.id]: {
                                                            ...prev.componentActions?.[component.id],
                                                            propertyBindings: {
                                                              ...prev.componentActions?.[component.id]?.propertyBindings,
                                                              [property]: {
                                                                ...bindingConfig,
                                                                mappingTable: { ...mappingTable, ranges: newRanges }
                                                              }
                                                            }
                                                          }
                                                        }
                                                      }));
                                                    }}
                                                    sx={{ width: 80 }}
                                                  />
                                                  <TextField
                                                    size="small"
                                                    label="Max"
                                                    type="number"
                                                    value={range.max || 100}
                                                    onChange={(e) => {
                                                      const newRanges = [...(mappingTable.ranges || [])];
                                                      newRanges[idx] = { ...newRanges[idx], max: Number(e.target.value) };
                                                      setWidgetMetadata(prev => ({
                                                        ...prev,
                                                        componentActions: {
                                                          ...prev.componentActions,
                                                          [component.id]: {
                                                            ...prev.componentActions?.[component.id],
                                                            propertyBindings: {
                                                              ...prev.componentActions?.[component.id]?.propertyBindings,
                                                              [property]: {
                                                                ...bindingConfig,
                                                                mappingTable: { ...mappingTable, ranges: newRanges }
                                                              }
                                                            }
                                                          }
                                                        }
                                                      }));
                                                    }}
                                                    sx={{ width: 80 }}
                                                  />
                                                  <TextField
                                                    size="small"
                                                    label="Output"
                                                    value={range.output || ''}
                                                    onChange={(e) => {
                                                      const newRanges = [...(mappingTable.ranges || [])];
                                                      newRanges[idx] = { ...newRanges[idx], output: e.target.value };
                                                      setWidgetMetadata(prev => ({
                                                        ...prev,
                                                        componentActions: {
                                                          ...prev.componentActions,
                                                          [component.id]: {
                                                            ...prev.componentActions?.[component.id],
                                                            propertyBindings: {
                                                              ...prev.componentActions?.[component.id]?.propertyBindings,
                                                              [property]: {
                                                                ...bindingConfig,
                                                                mappingTable: { ...mappingTable, ranges: newRanges }
                                                              }
                                                            }
                                                          }
                                                        }
                                                      }));
                                                    }}
                                                    sx={{ flex: 1 }}
                                                  />
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                      const newRanges = (mappingTable.ranges || []).filter((_, i) => i !== idx);
                                                      setWidgetMetadata(prev => ({
                                                        ...prev,
                                                        componentActions: {
                                                          ...prev.componentActions,
                                                          [component.id]: {
                                                            ...prev.componentActions?.[component.id],
                                                            propertyBindings: {
                                                              ...prev.componentActions?.[component.id]?.propertyBindings,
                                                              [property]: {
                                                                ...bindingConfig,
                                                                mappingTable: { ...mappingTable, ranges: newRanges }
                                                              }
                                                            }
                                                          }
                                                        }
                                                      }));
                                                    }}
                                                  >
                                                    🗑️
                                                  </IconButton>
                                                </Box>
                                              ))}
                                              <Button
                                                size="small"
                                                onClick={() => {
                                                  const newRanges = [...(mappingTable.ranges || []), { min: 0, max: 100, output: '' }];
                                                  setWidgetMetadata(prev => ({
                                                    ...prev,
                                                    componentActions: {
                                                      ...prev.componentActions,
                                                      [component.id]: {
                                                        ...prev.componentActions?.[component.id],
                                                        propertyBindings: {
                                                          ...prev.componentActions?.[component.id]?.propertyBindings,
                                                          [property]: {
                                                            ...bindingConfig,
                                                            mappingTable: { ...mappingTable, ranges: newRanges }
                                                          }
                                                        }
                                                      }
                                                    }
                                                  }));
                                                }}
                                                sx={{ mt: 1 }}
                                              >
                                                + Add Range
                                              </Button>
                                            </Box>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </Box>
                                </Paper>
                              ))}
                              <Button
                                size="small"
                                onClick={() => {
                                  setWidgetMetadata(prev => ({
                                    ...prev,
                                    componentActions: {
                                      ...prev.componentActions,
                                      [component.id]: {
                                        ...prev.componentActions?.[component.id],
                                        propertyBindings: {
                                          ...prev.componentActions?.[component.id]?.propertyBindings,
                                          'tagValue': '@inputs.'
                                        }
                                      }
                                    }
                                  }));
                                }}
                              >
                                + Add Property Binding
                              </Button>
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>

                  {/* Canvas Property Bindings */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                      🎨 Canvas Property Bindings
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Bind canvas properties to dynamic values from inputs or tags.
                    </Typography>

                    {Object.entries(widgetMetadata.canvasBindings || {}).map(([property, bindingConfig], index) => {
                      // Handle both old string format and new object format
                      const isOldFormat = typeof bindingConfig === 'string';
                      const binding = isOldFormat ? bindingConfig : bindingConfig?.source || '';
                      const bindingType = isOldFormat ? 'direct' : bindingConfig?.type || 'direct';
                      const mappingTable = isOldFormat ? {} : bindingConfig?.mappingTable || {};

                      return (
                        <Paper key={`canvas-${property}-${index}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                Canvas Binding #{index + 1}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setWidgetMetadata(prev => {
                                    const currentBindings = { ...prev.canvasBindings };
                                    delete currentBindings[property];
                                    return {
                                      ...prev,
                                      canvasBindings: currentBindings
                                    };
                                  });
                                }}
                              >
                                🗑️
                              </IconButton>
                            </Box>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Canvas Property</InputLabel>
                              <Select
                                value={property}
                                label="Canvas Property"
                                onChange={(e) => {
                                  const oldProperty = property;
                                  const newProperty = e.target.value;
                                  console.log('Canvas property changed:', { oldProperty, newProperty });

                                  if (oldProperty === newProperty) {
                                    return;
                                  }

                                  setWidgetMetadata(prev => {
                                    const currentBindings = { ...prev.canvasBindings };
                                    currentBindings[newProperty] = currentBindings[oldProperty];
                                    delete currentBindings[oldProperty];

                                    return {
                                      ...prev,
                                      canvasBindings: currentBindings
                                    };
                                  });
                                }}
                              >
                                <MenuItem value="backgroundColor">Background Color</MenuItem>
                                <MenuItem value="borderColor">Border Color</MenuItem>
                                <MenuItem value="borderWidth">Border Width</MenuItem>
                                <MenuItem value="borderRadius">Border Radius</MenuItem>
                                <MenuItem value="opacity">Opacity</MenuItem>
                              </Select>
                            </FormControl>

                            <FormControl size="small" fullWidth>
                              <InputLabel>Binding Type</InputLabel>
                              <Select
                                value={bindingType}
                                label="Binding Type"
                                onChange={(e) => {
                                  const newType = e.target.value;
                                  setWidgetMetadata(prev => ({
                                    ...prev,
                                    canvasBindings: {
                                      ...prev.canvasBindings,
                                      [property]: {
                                        type: newType,
                                        source: binding,
                                        mappingTable: newType === 'direct' ? {} : mappingTable
                                      }
                                    }
                                  }));
                                }}
                              >
                                <MenuItem value="direct">🔗 Direct Binding</MenuItem>
                                <MenuItem value="boolean">✅ Boolean Mapping</MenuItem>
                                <MenuItem value="range">📊 Range Mapping</MenuItem>
                                <MenuItem value="value">🏷️ Value Mapping</MenuItem>
                              </Select>
                            </FormControl>

                            <TextField
                              size="small"
                              label="Source"
                              value={binding}
                              onChange={(e) => {
                                setWidgetMetadata(prev => ({
                                  ...prev,
                                  canvasBindings: {
                                    ...prev.canvasBindings,
                                    [property]: isOldFormat ? e.target.value : {
                                      ...bindingConfig,
                                      source: e.target.value
                                    }
                                  }
                                }));
                              }}
                              placeholder="@inputs.backgroundColor"
                              helperText="Bind to @inputs.variableName or @tags[tagId]"
                              fullWidth
                            />

                            {/* Conditional Mapping Tables */}
                            {bindingType === 'boolean' && (
                              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Boolean Value Mapping</Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  <TextField
                                    size="small"
                                    label="True Value"
                                    value={mappingTable.true || ''}
                                    onChange={(e) => {
                                      setWidgetMetadata(prev => ({
                                        ...prev,
                                        canvasBindings: {
                                          ...prev.canvasBindings,
                                          [property]: {
                                            ...bindingConfig,
                                            mappingTable: { ...mappingTable, true: e.target.value }
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder="e.g., #ff0000"
                                    sx={{ flex: 1 }}
                                  />
                                  <TextField
                                    size="small"
                                    label="False Value"
                                    value={mappingTable.false || ''}
                                    onChange={(e) => {
                                      setWidgetMetadata(prev => ({
                                        ...prev,
                                        canvasBindings: {
                                          ...prev.canvasBindings,
                                          [property]: {
                                            ...bindingConfig,
                                            mappingTable: { ...mappingTable, false: e.target.value }
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder="e.g., #00ff00"
                                    sx={{ flex: 1 }}
                                  />
                                </Box>
                              </Box>
                            )}

                            {bindingType === 'range' && (
                              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Range Value Mapping</Typography>
                                {Object.entries(mappingTable).map(([rangeKey, rangeValue], idx) => (
                                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <TextField
                                      size="small"
                                      label="Range"
                                      value={rangeKey}
                                      onChange={(e) => {
                                        const newMappingTable = { ...mappingTable };
                                        delete newMappingTable[rangeKey];
                                        newMappingTable[e.target.value] = rangeValue;
                                        setWidgetMetadata(prev => ({
                                          ...prev,
                                          canvasBindings: {
                                            ...prev.canvasBindings,
                                            [property]: {
                                              ...bindingConfig,
                                              mappingTable: newMappingTable
                                            }
                                          }
                                        }));
                                      }}
                                      placeholder="e.g., <10, 10-20, >20"
                                      sx={{ flex: 1 }}
                                    />
                                    <TextField
                                      size="small"
                                      label="Value"
                                      value={rangeValue}
                                      onChange={(e) => {
                                        setWidgetMetadata(prev => ({
                                          ...prev,
                                          canvasBindings: {
                                            ...prev.canvasBindings,
                                            [property]: {
                                              ...bindingConfig,
                                              mappingTable: { ...mappingTable, [rangeKey]: e.target.value }
                                            }
                                          }
                                        }));
                                      }}
                                      placeholder="e.g., #ff0000"
                                      sx={{ flex: 1 }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const newMappingTable = { ...mappingTable };
                                        delete newMappingTable[rangeKey];
                                        setWidgetMetadata(prev => ({
                                          ...prev,
                                          canvasBindings: {
                                            ...prev.canvasBindings,
                                            [property]: {
                                              ...bindingConfig,
                                              mappingTable: newMappingTable
                                            }
                                          }
                                        }));
                                      }}
                                    >
                                      ❌
                                    </IconButton>
                                  </Box>
                                ))}
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setWidgetMetadata(prev => ({
                                      ...prev,
                                      canvasBindings: {
                                        ...prev.canvasBindings,
                                        [property]: {
                                          ...bindingConfig,
                                          mappingTable: { ...mappingTable, '<10': '' }
                                        }
                                      }
                                    }));
                                  }}
                                >
                                  + Add Range
                                </Button>
                              </Box>
                            )}

                            {bindingType === 'value' && (
                              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Value Mapping</Typography>
                                {Object.entries(mappingTable).map(([mapKey, mapValue], idx) => (
                                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <TextField
                                      size="small"
                                      label="Input Value"
                                      value={mapKey}
                                      onChange={(e) => {
                                        const newMappingTable = { ...mappingTable };
                                        delete newMappingTable[mapKey];
                                        newMappingTable[e.target.value] = mapValue;
                                        setWidgetMetadata(prev => ({
                                          ...prev,
                                          canvasBindings: {
                                            ...prev.canvasBindings,
                                            [property]: {
                                              ...bindingConfig,
                                              mappingTable: newMappingTable
                                            }
                                          }
                                        }));
                                      }}
                                      placeholder="e.g., 1, 2, 3"
                                      sx={{ flex: 1 }}
                                    />
                                    <TextField
                                      size="small"
                                      label="Output Value"
                                      value={mapValue}
                                      onChange={(e) => {
                                        setWidgetMetadata(prev => ({
                                          ...prev,
                                          canvasBindings: {
                                            ...prev.canvasBindings,
                                            [property]: {
                                              ...bindingConfig,
                                              mappingTable: { ...mappingTable, [mapKey]: e.target.value }
                                            }
                                          }
                                        }));
                                      }}
                                      placeholder="e.g., #ff0000"
                                      sx={{ flex: 1 }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const newMappingTable = { ...mappingTable };
                                        delete newMappingTable[mapKey];
                                        setWidgetMetadata(prev => ({
                                          ...prev,
                                          canvasBindings: {
                                            ...prev.canvasBindings,
                                            [property]: {
                                              ...bindingConfig,
                                              mappingTable: newMappingTable
                                            }
                                          }
                                        }));
                                      }}
                                    >
                                      ❌
                                    </IconButton>
                                  </Box>
                                ))}
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setWidgetMetadata(prev => ({
                                      ...prev,
                                      canvasBindings: {
                                        ...prev.canvasBindings,
                                        [property]: {
                                          ...bindingConfig,
                                          mappingTable: { ...mappingTable, '1': '' }
                                        }
                                      }
                                    }));
                                  }}
                                >
                                  + Add Value Mapping
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      );
                    })}
                    <Button
                      size="small"
                      onClick={() => {
                        setWidgetMetadata(prev => ({
                          ...prev,
                          canvasBindings: {
                            ...prev.canvasBindings,
                            'backgroundColor': {
                              type: 'direct',
                              source: '@inputs.',
                              mappingTable: {}
                            }
                          }
                        }));
                      }}
                    >
                      + Add Canvas Property Binding
                    </Button>
                  </Box>

                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Components */}
            {/* Component Categories */}
            {Object.entries(componentCategories).map(([categoryName, categoryComponents]) => (
              <Accordion key={categoryName} defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography variant="subtitle1">
                    {categoryName} ({categoryComponents.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {categoryComponents.map(type => (
                      <Grid item xs={6} key={type.id}>
                        <Button
                          variant="outlined"
                          size="small"
                          draggable
                          onClick={() => handleAddComponent(type.id)}
                          onDragStart={(e) => {
                            e.dataTransfer.setData('componentType', type.id);
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                          sx={{ 
                            width: '100%', 
                            minHeight: 40,
                            flexDirection: 'column',
                            fontSize: '0.75rem',
                            cursor: 'grab',
                            '&:active': {
                              cursor: 'grabbing'
                            },
                            ...(type.isCustom && {
                              borderColor: 'primary.main',
                              backgroundColor: 'primary.50',
                              '&:hover': {
                                backgroundColor: 'primary.100',
                                borderColor: 'primary.dark',
                              }
                            })
                          }}
                          title={`Click to add or drag to canvas: ${type.name}`}
                        >
                          <span style={{ fontSize: '16px' }}>{type.icon}</span>
                          {type.name}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Paper>

        {/* Canvas */}
        <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'auto', backgroundColor: '#f5f5f5' }}>
          <Box
            sx={{
              position: 'relative',
              width: canvasSize.width,
              height: canvasSize.height,
              margin: '20px auto',
              backgroundColor: canvasProperties.backgroundColor,
              border: `${canvasProperties.borderWidth}px solid ${canvasProperties.borderColor}`,
              borderRadius: `${canvasProperties.borderRadius}px`,
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              backgroundImage: canvasProperties.showGrid ? 
                `radial-gradient(circle, ${canvasProperties.gridColor} 1px, transparent 1px)` : 'none',
              backgroundSize: canvasProperties.showGrid ? `${canvasProperties.gridSize}px ${canvasProperties.gridSize}px` : 'none'
            }}
            onClick={() => {
              if (!groupMode) {
                setSelectedId(null);
                setSelectedGroup([]);
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={(e) => {
              e.preventDefault();
              const componentType = e.dataTransfer.getData('componentType');
              if (componentType) {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = (e.clientX - rect.left) / (zoom / 100) - 50; // Offset for center positioning
                const y = (e.clientY - rect.top) / (zoom / 100) - 25;
                handleAddComponentAt(componentType, Math.max(0, x), Math.max(0, y));
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                componentId: null // Canvas context menu
              });
            }}
          >
            {components.map(renderComponent)}

            {/* Snap Guide Lines */}
            {snapLines.vertical.map((line, i) => {
              // Color based on alignment type
              const color = line.type === 'left' ? '#4caf50' :      // Green for left edge
                           line.type === 'right' ? '#2196f3' :       // Blue for right edge
                           line.type === 'centerX' ? '#9c27b0' :     // Purple for center
                           '#2196f3';
              return (
                <Box
                  key={`snap-v-${i}`}
                  sx={{
                    position: 'absolute',
                    left: line.x,
                    top: 0,
                    width: line.type === 'centerX' ? '2px' : '1px',
                    height: '100%',
                    backgroundColor: color,
                    pointerEvents: 'none',
                    zIndex: 10000,
                    opacity: 0.8
                  }}
                />
              );
            })}
            {snapLines.horizontal.map((line, i) => {
              // Color based on alignment type
              const color = line.type === 'top' ? '#ff9800' :        // Orange for top edge
                           line.type === 'bottom' ? '#f44336' :       // Red for bottom edge
                           line.type === 'centerY' ? '#9c27b0' :      // Purple for center
                           '#2196f3';
              return (
                <Box
                  key={`snap-h-${i}`}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: line.y,
                    width: '100%',
                    height: line.type === 'centerY' ? '2px' : '1px',
                    backgroundColor: color,
                    pointerEvents: 'none',
                    zIndex: 10000,
                    opacity: 0.8
                  }}
                />
              );
            })}

            {/* Group mode overlay */}
            {groupMode && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  border: '2px dashed #1976d2',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h6" color="primary">
                  Group Mode - Click components to select
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Properties Panel */}
        <Paper sx={{ width: 320, borderRadius: 0, overflowY: 'auto' }}>
          {selectedId ? (
            <SimplePropertyInspector
              widget={components.find(c => c.id === selectedId)}
              onChange={(updatedWidget) => {
                setComponents(prev => prev.map(comp =>
                  comp.id === selectedId ? { ...comp, ...updatedWidget, _updateKey: Date.now() } : comp
                ));
              }}
            />
          ) : (
            <CanvasPropertyInspector
              canvasSize={canvasSize}
              canvasProperties={canvasProperties}
              onSizeChange={setCanvasSize}
              onPropertyChange={setCanvasProperties}
            />
          )}
        </Paper>
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu.visible}
        onClose={() => setContextMenu(prev => ({ ...prev, visible: false }))}
        anchorReference="anchorPosition"
        anchorPosition={{ top: contextMenu.y, left: contextMenu.x }}
      >
        {contextMenu.componentId ? (
          // Component context menu
          <>
            <MenuItem onClick={() => {
              // Copy component
              const comp = components.find(c => c.id === contextMenu.componentId);
              if (comp) setClipboard({ ...comp, id: undefined }); // Remove ID for pasting
              setContextMenu(prev => ({ ...prev, visible: false }));
            }}>
              <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Copy</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              // Duplicate component
              const comp = components.find(c => c.id === contextMenu.componentId);
              if (comp) {
                const newComponent = {
                  ...comp,
                  id: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  x: comp.x + 20,
                  y: comp.y + 20,
                  zIndex: components.length + 1
                };
                setComponents(prev => [...prev, newComponent]);
                setSelectedId(newComponent.id);
              }
              setContextMenu(prev => ({ ...prev, visible: false }));
            }}>
              <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              // Bring to front
              setComponents(prev => prev.map(c => 
                c.id === contextMenu.componentId 
                  ? { ...c, zIndex: Math.max(...prev.map(p => p.zIndex)) + 1 }
                  : c
              ));
              setContextMenu(prev => ({ ...prev, visible: false }));
            }}>
              <ListItemIcon><BringToFrontIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Bring to Front</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              // Send to back
              setComponents(prev => prev.map(c => 
                c.id === contextMenu.componentId 
                  ? { ...c, zIndex: Math.min(...prev.map(p => p.zIndex)) - 1 }
                  : c
              ));
              setContextMenu(prev => ({ ...prev, visible: false }));
            }}>
              <ListItemIcon><SendToBackIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Send to Back</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              // Delete component
              setComponents(prev => prev.filter(c => c.id !== contextMenu.componentId));
              setContextMenu(prev => ({ ...prev, visible: false }));
              if (selectedId === contextMenu.componentId) setSelectedId(null);
            }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        ) : (
          // Canvas context menu
          <>
            <MenuItem 
              onClick={() => {
                // Paste component
                if (clipboard) {
                  const newComponent = {
                    ...clipboard,
                    id: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    x: Math.max(0, (contextMenu.x - 300) / (zoom / 100)), // Adjust for canvas position
                    y: Math.max(0, (contextMenu.y - 100) / (zoom / 100)), // Adjust for canvas position
                    zIndex: components.length + 1
                  };
                  setComponents(prev => [...prev, newComponent]);
                  setSelectedId(newComponent.id);
                }
                setContextMenu(prev => ({ ...prev, visible: false }));
              }}
              disabled={!clipboard}
            >
              <ListItemIcon><PasteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Paste</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              // Select all components
              if (components.length > 0) {
                setSelectedGroup(components.map(c => c.id));
                setGroupMode(true);
              }
              setContextMenu(prev => ({ ...prev, visible: false }));
            }}>
              <ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Select All</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default WidgetDesigner;